import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useOutletContext, useSearchParams } from 'react-router';
import { useFormFactor } from '@/lib/formFactor';
import { api, ApiError, withQuery } from '@/api/client';
import { useSession } from '@/stores/session';
import { useNetwork } from '@/stores/network';
import { Icon } from '@/components/Icon';
import { Dot } from '@/components/Tag';
import { Button, IconButton } from '@/components/Button';
import { Empty, ErrorState, Skeleton, WifiOffMark } from '@/components/States';
import { orderStatus, stockStatus } from '@/lib/status';
import { money } from '@/lib/format';
import { approveOrderWithFollower, assignOrderFollower, rejectOrder, FILTER_KEYS, VIEWS, useOrderList, useResource, type Filters, type ViewKey } from './api';
import type { OrderListItem, OrderPage } from '@/api/types';
import { DEFAULT_WORK_CARD_SETTINGS, LedgerCard, WorkCard, summarizeLines, type WorkCardSettings } from './OrderCards';
import { OrderCardSettingsSheet } from './OrderCardSettingsSheet';
import { SwipeOrderCard } from './SwipeOrderCard';
import { OrdersTable, LEDGER_PRESETS, ledgerColumnChoices, type LedgerPreset } from './OrdersTable';
import { LedgerColumnsPopover } from './LedgerColumnsPopover';
import { loadLedgerColumns, localLedgerColumns, saveLedgerColumns } from './ledgerColumns';
import { FilterPanel } from './FilterPanel';
import { OrderDetail } from './OrderDetail';
import { OrderLedgerActions } from './OrderLedgerActions';
import { ViewSidebar } from '@/components/ViewSidebar';
import s from './OrdersPage.module.css';

const FILTER_LABEL: Record<string, (v: string) => string> = {
  status: (v) => orderStatus[v as keyof typeof orderStatus]?.label || v,
  stockStatus: (v) => ({ READY: '配货齐全', PARTIAL: '部分可发', SHORTAGE: '缺货', COMPLETED: '无待发' })[v] || v,
  shop: (v) => v,
  assignment: (v) => (v === 'assigned' ? '已分配跟单' : '未分配跟单'),
  exceptionStatus: (v) => ({ has: '有异常', open: '异常未闭环', PENDING: '异常待处理', PROCESSING: '异常处理中', RESOLVED: '异常已处理', CLOSED: '异常已关闭' })[v] || v,
  invoiceStatus: (v) => ({ NOT_REQUIRED: '无需开票', PENDING: '待开票', ISSUED: '已开票' })[v] || v,
  changeState: (v) => ({ changed: '有变更', pendingReview: '变更待审核', pendingAcknowledgement: '变更待知晓', acknowledged: '变更已知晓' })[v] || v,
  dateField: (v) => v === 'deliveryDate' ? '按交货期' : '按下单日期',
  dateFrom: (v) => `起 ${v}`,
  dateTo: (v) => `止 ${v}`,
};

export function OrdersPage() {
  const ff = useFormFactor();
  const navigate = useNavigate();
  const outlet = useOutletContext<{ setPhoneTabBarHidden: (hidden: boolean) => void } | undefined>();
  const can = useSession((st) => st.can);
  const userId = useSession((st) => st.user?.id);
  const isAdmin = useSession((st) => st.user?.role === 'ADMIN');
  const hideSalesUser = useSession((st) => st.user?.restrictions?.includes('不能查看具体业务员') ?? false);
  const [sp, setSp] = useSearchParams();
  const view = (sp.get('view') as ViewKey) || 'tasks';
  const keyword = sp.get('q') || '';
  const selectedId = sp.get('id') || undefined;
  const selectionScope = new URLSearchParams(sp);
  selectionScope.delete('id');
  const filters: Filters = Object.fromEntries(FILTER_KEYS.map((k) => [k, sp.get(k) || undefined]).filter(([, v]) => v));
  if ((filters.dateFrom || filters.dateTo) && !filters.dateField) filters.dateField = 'customerOrderDate';
  const [panel, setPanel] = useState(false);
  const [draftQ, setDraftQ] = useState(keyword);
  const [searching, setSearching] = useState(!!keyword || sp.get('search') === '1');
  const [listCollapsed, setListCollapsed] = useState(false);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [tabletViewOpen, setTabletViewOpen] = useState(false);
  const [desktopExtraViewsOpen, setDesktopExtraViewsOpen] = useState(false);
  const tabletViewRef = useRef<HTMLDivElement>(null);
  const [ledgerKeys, setLedgerKeys] = useState<string[] | null>(null);
  const [multi, setMulti] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [selectedExtra, setSelectedExtra] = useState<OrderListItem[]>([]);
  const [selectingAll, setSelectingAll] = useState(false);
  const [selectAllPromptOpen, setSelectAllPromptOpen] = useState(false);
  const [followers, setFollowers] = useState<{ id: string; name: string }[]>([]);
  const [followerId, setFollowerId] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkError, setBulkError] = useState('');
  const [bulkDone, setBulkDone] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [bulkMoreOpen, setBulkMoreOpen] = useState(false);
  const [cardSettingsOpen, setCardSettingsOpen] = useState(false);
  const [cardSettings, setCardSettings] = useState<WorkCardSettings>(DEFAULT_WORK_CARD_SETTINGS);
  const [swipedOrderId, setSwipedOrderId] = useState<string | null>(null);
  const [ledgerActionId, setLedgerActionId] = useState<string | null>(null);

  useEffect(() => {
    if (ff !== 'phone' || !outlet) return;
    outlet.setPhoneTabBarHidden(multi);
    return () => outlet.setPhoneTabBarHidden(false);
  }, [ff, multi, outlet?.setPhoneTabBarHidden]);

  useEffect(() => { setMulti(false); setSelectedIds(new Set()); setSelectedExtra([]); setSwipedOrderId(null); setBulkError(''); setBulkDone(''); }, [selectionScope.toString()]);
  useEffect(() => {
    if (!multi || !can('orders.approve') || !can('orders.assign')) return;
    const controller = new AbortController();
    void api.get<{ followers: { id: string; name: string }[] }>('/api/users', undefined, controller.signal)
      .then((result) => { setFollowers(result.followers || []); setFollowerId((old) => old || result.followers?.[0]?.id || ''); })
      .catch(() => { /* 审核按钮在没有跟单员时保持禁用 */ });
    return () => controller.abort();
  }, [multi, can]);

  useEffect(() => {
    if (!userId) { setLedgerKeys(null); return; }
    let active = true;
    setLedgerKeys(localLedgerColumns(userId));
    void loadLedgerColumns(userId).then((keys) => { if (active) setLedgerKeys(keys); });
    return () => { active = false; };
  }, [userId]);
  useEffect(() => {
    if (!userId) { setCardSettings(DEFAULT_WORK_CARD_SETTINGS); return; }
    try {
      const stored: unknown = JSON.parse(localStorage.getItem(`v2:order-card:${userId}`) || 'null');
      if (!stored || typeof stored !== 'object' || Array.isArray(stored)) { setCardSettings(DEFAULT_WORK_CARD_SETTINGS); return; }
      const values = stored as Record<string, unknown>;
      setCardSettings(Object.fromEntries(Object.entries(DEFAULT_WORK_CARD_SETTINGS).map(([key, defaultValue]) => [key, typeof values[key] === 'boolean' ? values[key] : defaultValue])) as WorkCardSettings);
    } catch { setCardSettings(DEFAULT_WORK_CARD_SETTINGS); }
  }, [userId]);

  const views = VIEWS.filter((v) => !v.perms || can(...v.perms));
  const viewDef = views.find((v) => v.key === view) || views[0];
  const query = useMemo(() => ({
    ...viewDef.query,
    ...filters,
    keyword: keyword || undefined,
  }), [viewDef, JSON.stringify(filters), keyword]); // eslint-disable-line react-hooks/exhaustive-deps
  const ledger = view === 'all';
  const dateActive = Boolean(filters.dateFrom || filters.dateTo);
  const rawPreset = sp.get('cols');
  const ledgerPreset: LedgerPreset = rawPreset === 'recon' || rawPreset === 'plan' || rawPreset === 'ship' ? rawPreset : 'full';
  const listPath = viewDef.key === 'tasks' ? '/api/v2/orders/tasks' : dateActive ? '/api/v2/orders/ledger' : '/api/orders';
  const pageSize = ff === 'desktop' ? 30 : 20;
  const list = useOrderList(query, pageSize, ff === 'phone' || (ff === 'tablet' && !ledger), listPath);
  const listRequest = withQuery(listPath, { ...query, page: 1, pageSize, includeStock: 1, includeStats: 1 });
  const desktopFailedTasks = ff === 'desktop' && viewDef.key === 'tasks' && !!list.error && !list.items.length;
  const tasksErrorPageActive = viewDef.key === 'tasks' && !list.items.length && (list.loading || !!list.error);
  useEffect(() => {
    useNetwork.getState().setOrdersListErrorPage(tasksErrorPageActive);
    return () => useNetwork.getState().setOrdersListErrorPage(false);
  }, [tasksErrorPageActive]);
  useEffect(() => { if ((ff === 'tablet' && ledger) || ff === 'desktop') setSelectedIds(new Set()); }, [ff, ledger, list.page]);

  useEffect(() => {
    if (ff !== 'tablet' || ledger || multi || list.loading || !list.current || !list.items.length || list.items.some((item) => item.id === selectedId)) return;
    const next = new URLSearchParams(sp);
    next.set('id', list.items[0].id);
    setSp(next, { replace: true });
  }, [ff, ledger, list.loading, list.current, list.items, multi, selectedId, setSp, sp]);

  useEffect(() => {
    if (ff !== 'tablet' || ledger || multi || !selectedId) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== '[' || event.metaKey || event.ctrlKey || event.altKey || event.repeat) return;
      const target = event.target;
      if (target instanceof HTMLElement && (target.isContentEditable || target.closest('input, textarea, select, [contenteditable="true"]'))) return;
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      event.preventDefault();
      setListCollapsed((value) => !value);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ff, ledger, multi, selectedId]);
  const taskCount = useResource<OrderPage>(withQuery('/api/v2/orders/tasks', { keyword, pageSize: 1 }));
  const taskBaseStats = useResource<OrderPage>(viewDef.key === 'tasks' ? withQuery('/api/orders', { keyword, pageSize: 1, includeStats: 1 }) : null);
  const stats = viewDef.key === 'tasks' ? taskBaseStats.data?.stats : list.stats;

  useEffect(() => {
    if (!tabletViewOpen) return;
    const close = (event: PointerEvent) => { if (!tabletViewRef.current?.contains(event.target as Node)) setTabletViewOpen(false); };
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') setTabletViewOpen(false); };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('pointerdown', close); document.removeEventListener('keydown', escape); };
  }, [tabletViewOpen]);

  const setParam = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(sp);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setSp(next, { replace: true });
  };

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey || event.repeat) return;
      const target = event.target;
      if (target instanceof HTMLElement && (target.isContentEditable || target.closest('input, textarea, select, [contenteditable="true"]'))) return;
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      const key = event.key.toLowerCase();
      if (key === 'f') { event.preventDefault(); setPanel(true); }
      if (key === 'r') { event.preventDefault(); void list.reload(); }
      if (key === 'v') {
        const index = views.findIndex((item) => item.key === viewDef.key);
        const next = views[(index + 1) % views.length];
        if (next) { event.preventDefault(); setParam({ view: next.key === 'tasks' ? undefined : next.key, id: undefined }); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [list.reload, setParam, viewDef.key, views]);

  // iPad 外接键盘：在已加载的工作卡之间移动，专注模式使用 Shift+J/K。
  useEffect(() => {
    if (ff !== 'tablet' || ledger || multi) return;
    const onKey = (event: KeyboardEvent) => {
      if (!['j', 'k'].includes(event.key.toLowerCase()) || event.metaKey || event.ctrlKey || event.altKey || event.repeat) return;
      if (listCollapsed && !event.shiftKey) return;
      const target = event.target;
      if (target instanceof HTMLElement && (target.isContentEditable || target.closest('input, textarea, select, [contenteditable="true"]'))) return;
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      const index = list.items.findIndex((item) => item.id === selectedId);
      const nextIndex = Math.max(0, Math.min(list.items.length - 1, index + (event.key.toLowerCase() === 'j' ? 1 : -1)));
      const nextId = list.items[nextIndex]?.id;
      if (!nextId || nextId === selectedId) return;
      event.preventDefault();
      const next = new URLSearchParams(sp);
      next.set('id', nextId);
      setSp(next, { replace: true });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ff, ledger, list.items, listCollapsed, multi, selectedId, setSp, sp]);

  useEffect(() => {
    if (ff !== 'tablet' || !selectedId || listCollapsed) return;
    const selected = Array.from(document.querySelectorAll<HTMLElement>('[data-order-id]')).find((node) => node.dataset.orderId === selectedId);
    selected?.scrollIntoView({ block: 'nearest' });
  }, [ff, list.items, listCollapsed, selectedId]);

  // 选中的视图 chip 滚到可见位置（横滑条里可能在屏幕外）
  useEffect(() => {
    document.querySelectorAll(`.${s.chips} .${s.on}`).forEach((el) => el.scrollIntoView({ block: 'nearest', inline: 'center' }));
  }, [viewDef.key, ff]);

  // 手机 / iPad：滚到底自动加载下一页
  const sentinel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinel.current;
    if (!el || ff === 'desktop' || ledger) return;
    const io = new IntersectionObserver((es) => es[0].isIntersecting && list.loadMore(), { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  });

  const activeFilters = Object.entries(filters).filter(([, v]) => v) as [keyof Filters, string][];
  const nonDateFilters = activeFilters.filter(([key]) => !['dateField', 'dateFrom', 'dateTo'].includes(key));
  const dateRangeLabel = (() => {
    const { dateFrom, dateTo, dateField } = filters;
    if (!dateFrom && !dateTo) return undefined;
    const dateName = dateField === 'deliveryDate' ? '交货期' : '下单';
    if (dateFrom && dateTo && dateFrom.slice(0, 7) === dateTo.slice(0, 7)) {
      const [year, month] = dateFrom.split('-').map(Number);
      const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
      if (dateFrom === `${year}-${String(month).padStart(2, '0')}-01` && dateTo.endsWith(`-${String(lastDay).padStart(2, '0')}`)) {
        return `${month} 月${dateName}`;
      }
    }
    const range = [dateFrom && `起 ${dateFrom}`, dateTo && `止 ${dateTo}`].filter(Boolean).join(' · ');
    return `${dateName === '下单' ? '下单日期' : dateName} ${range}`;
  })();
  const activeFilterCount = nonDateFilters.length + (dateRangeLabel ? 1 : 0);
  const phoneViewOrder: ViewKey[] = ['tasks', 'review', 'ready', 'assembling', 'shortage', 'changes', 'exceptions', 'all'];
  const phoneViews = ff === 'phone'
    ? [...phoneViewOrder.map((key) => views.find((item) => item.key === key)).filter((item): item is typeof views[number] => !!item), ...views.filter((item) => !phoneViewOrder.includes(item.key))]
    : views;
  const chips = (
    <div className={s.chips}>
      {phoneViews.map((v) => {
        const n = dateActive ? (v.key === viewDef.key ? list.total : -1) : v.key === 'tasks' ? (viewDef.key === 'tasks' ? list.total : taskCount.data?.pagination.total ?? -1) : stats ? v.count(stats) : -1;
        return (
          <button key={v.key} className={`${s.chip} ${v.key === viewDef.key ? s.on : ''}`} onClick={() => setParam({ view: v.key === 'tasks' ? undefined : v.key, id: undefined })}>
            {ff === 'phone' && v.key === 'shortage' ? '缺货待采' : v.label}
            {n >= 0 ? <i className="tnum">{n}</i> : list.loading && !list.items.length ? <i>…</i> : null}
          </button>
        );
      })}
    </div>
  );
  const tabletViewPicker = <div className={s.tabletViewPicker} ref={tabletViewRef}>
    <button type="button" aria-haspopup="menu" aria-expanded={tabletViewOpen} onClick={() => setTabletViewOpen((open) => !open)}><span>{viewDef.label}</span><small className="tnum">{list.loading && !list.items.length ? '…' : list.error && !list.items.length ? '—' : list.total}</small><Icon name="chev" size={14} /></button>
    {tabletViewOpen && <div className={s.tabletViewMenu} role="menu" aria-label="订单视图">{views.map((v) => {
      const count = dateActive ? (v.key === viewDef.key ? list.total : -1) : v.key === 'tasks' ? (viewDef.key === 'tasks' ? list.total : taskCount.data?.pagination.total ?? -1) : stats ? v.count(stats) : -1;
      return <button type="button" role="menuitemradio" aria-checked={v.key === viewDef.key} key={v.key} onClick={() => { setTabletViewOpen(false); setParam({ view: v.key === 'tasks' ? undefined : v.key, id: undefined }); }}><span>{v.label}</span>{count >= 0 && <small className="tnum">{count}</small>}</button>;
    })}</div>}
  </div>;
  const filterBtn = (
    <button className={`${s.fbtn} ${activeFilterCount ? s.fon : ''}`} onClick={() => setPanel(true)}>
      <Icon name="filter" />筛选{activeFilterCount > 0 && <span className={s.fbadge}>{activeFilterCount}</span>}
    </button>
  );
  const applied = activeFilters.length > 0 && (
    <div className={s.applied}>
      {nonDateFilters.map(([k, v]) => (
        <button key={k} className={s.achip} onClick={() => setParam({ [k]: undefined })}>
          {FILTER_LABEL[k]?.(v) || v}<Icon name="x" size={14} />
        </button>
      ))}
      {dateRangeLabel && <button className={s.achip} onClick={() => setParam({ dateField: undefined, dateFrom: undefined, dateTo: undefined })}>
        {dateRangeLabel}<Icon name="x" size={14} />
      </button>}
      <button className={s.clr} onClick={() => setParam(Object.fromEntries(FILTER_KEYS.map((k) => [k, undefined])))}>清空</button>
    </div>
  );
  const searchBox = (
    <form
      className={s.search}
      onSubmit={(e) => {
        e.preventDefault();
        setParam({ q: draftQ.trim() || undefined, id: undefined });
      }}
    >
      <Icon name="srch" />
      <input value={draftQ} onChange={(e) => setDraftQ(e.target.value)} placeholder="订单号 / 1688 单号 / 客户 / 产品" enterKeyHint="search" />
      {draftQ && <button type="button" className={s.clearQ} onClick={() => { setDraftQ(''); setParam({ q: undefined }); }}><Icon name="x" size={14} /></button>}
    </form>
  );
  const newOrder = can('orders.create') && (
    <Link to="/orders/new" className={s.newBtn}>
      <Icon name="plus" />新建订单
    </Link>
  );
  const tabletNewOrder = can('orders.create') && <Link to="/orders/new" className={s.tabletNewOrder} aria-label="新建订单"><Icon name="plus" size={18} /><span>新建</span></Link>;
  const columnControl = ledger && ff !== 'phone' && <div className={s.columnWrap}>
    <button type="button" className={s.columnBtn} aria-expanded={columnsOpen} onClick={() => setColumnsOpen((open) => !open)}><Icon name="cog2" />列设置</button>
    {columnsOpen && <LedgerColumnsPopover modal={ff === 'desktop'} touch={ff === 'tablet'} canSaveAll={isAdmin && import.meta.env.VITE_READONLY === '0'} choices={ledgerColumnChoices(can('orders.amount.read'), !hideSalesUser, ff === 'tablet')} savedKeys={ledgerKeys} onClose={() => setColumnsOpen(false)} onSave={async (keys, scope) => {
      if (!userId) throw new Error('当前账号不可用');
      await saveLedgerColumns(userId, keys, scope);
      setLedgerKeys(await loadLedgerColumns(userId));
    }} />}
  </div>;

  const body = (() => {
    if (list.error && !list.items.length) {
      if (ff === 'phone') return <div className={s.phoneOrderError}>
        <WifiOffMark />
        <h4>订单列表加载失败</h4>
        <p>{list.error instanceof ApiError && list.error.status === 0 ? '网络连接失败，请检查网络后重试。' : list.error.message}</p>
        {list.error instanceof ApiError && list.error.status === 0 && <div className={s.phoneRetryInfo}><b>已自动重试 3 次，仍未成功</b><span>间隔 0、250、750 毫秒</span></div>}
        <details className={s.phoneErrorCode}><summary>GET {listRequest}</summary><code>GET {listRequest}</code></details>
        <div className={s.phoneErrorActions}><button type="button" onClick={list.reload}>重试</button><Link to="/">返回主屏</Link></div>
      </div>;
      if (ff === 'desktop') return <div className={s.orderState}><div className={s.desktopError}>
        <div className={s.desktopWarnIcon}><Icon name="alert" size={20} /></div>
        <h4>订单列表加载失败</h4>
        <p>{list.error instanceof ApiError && list.error.status === 0 ? '网络连接失败，请检查网络或刷新页面后重试。' : list.error.message}</p>
        {list.error instanceof ApiError && list.error.status === 0 && <p>系统已自动重试 3 次（间隔 0、250、750 毫秒），仍未成功。</p>}
        <code>GET {listRequest}</code>
        <div className={s.desktopErrorActions}><button type="button" onClick={list.reload}>重试</button><Link to="/">返回主屏</Link></div>
        <p className={s.desktopErrorFootnote}>这是独立的错误页，不是弹个提示后留白 —— 用户能看到失败的是什么、重试过几次、下一步做什么。</p>
      </div></div>;
      if (ff === 'tablet') return <div className={`${s.orderState} ${s.tabletOrderError}`}><div className={s.tabletErrorContent}>
        <WifiOffMark className={s.tabletErrorMark} />
        <h4>订单列表加载失败</h4>
        <p>{list.error instanceof ApiError && list.error.status === 0 ? '网络连接失败，请检查网络后重试。' : list.error.message}</p>
        {list.error instanceof ApiError && list.error.status === 0 && <div className={s.tabletRetryInfo}><Icon name="info" size={16} /><div><strong>已自动重试 3 次，仍未成功</strong><p>间隔 0、250、750 毫秒</p></div></div>}
        <code className={s.tabletErrorCode}>GET {listRequest}</code>
        <div className={s.tabletErrorActions}><Link to="/">返回主屏</Link><button type="button" onClick={list.reload}>重试</button></div>
      </div></div>;
      return <div className={s.orderState}>
        <ErrorState title="订单列表加载失败" message={list.error instanceof ApiError && list.error.status === 0 ? '网络连接失败，请检查网络后重试。' : list.error.message} detail={`GET ${listRequest}`} onRetry={list.reload} />
        {list.error instanceof ApiError && list.error.status === 0 && <p className={s.retryNote}>已自动尝试 3 次，仍未成功（首次立即请求，随后间隔 250、750 毫秒）。</p>}
        <Link to="/" className={s.emptyAction}>返回主屏</Link>
      </div>;
    }
    if (list.loading && !list.items.length) return ff === 'desktop'
      ? <div className={s.orderTableSkeleton} aria-label="订单列表加载中">
        <div className={s.skeletonColumns}>{Array.from({ length: 6 }, (_, index) => <i key={index} />)}</div>
        {Array.from({ length: 5 }, (_, row) => <div className={s.skeletonRow} key={row} style={{ opacity: row === 3 ? 0.6 : row === 4 ? 0.3 : undefined }}>
          {Array.from({ length: 6 }, (_, column) => <i key={column} />)}
        </div>)}
      </div>
      : <div className={s.orderCardSkeleton} aria-label="订单列表加载中">{Array.from({ length: 3 }, (_, index) => <Skeleton key={index} lines={4} />)}</div>;
    if (!list.items.length) {
      const filtered = !!keyword || activeFilters.length > 0;
      const awaitingReview = viewDef.key === 'review' && !filtered;
      return (
        <div className={`${s.orderState} ${awaitingReview ? s.reviewEmpty : ''}`}><Empty icon="ok" title={awaitingReview ? '没有等你审核的订单' : filtered ? '没有符合条件的订单' : `「${viewDef.label}」里没有订单`} action={
          <button type="button" className={s.emptyAction} onClick={() => filtered
            ? setParam({ ...Object.fromEntries(FILTER_KEYS.map((key) => [key, undefined])), q: undefined })
            : setParam({ view: viewDef.key === 'tasks' ? 'all' : undefined, id: undefined })}>
            {awaitingReview ? '去我的待办' : filtered ? '清空筛选' : viewDef.key === 'tasks' ? '查看全部订单' : '去我的待办'}
          </button>}>
          {awaitingReview ? '业务员提交预占后，订单会出现在这里。你也可以去「我的待办」看看其他需要推进的单子。' : filtered ? '换个关键词，或者清空筛选再看看。' : '别的视图里可能有需要推进的单子。'}
        </Empty></div>
      );
    }
    return null;
  })();

  const panelEl = panel && (
    <FilterPanel
      value={filters}
      stats={stats}
      view={viewDef.key}
      viewQuery={viewDef.query}
      keyword={keyword}
      sheet={ff === 'phone'}
      onClose={() => setPanel(false)}
      onApply={(f) => {
        setPanel(false);
        setParam({ ...Object.fromEntries(FILTER_KEYS.map((k) => [k, f[k]])), id: undefined });
      }}
    />
  );

  const selectedItems = [...list.items, ...selectedExtra.filter((order) => !list.items.some((loaded) => loaded.id === order.id))].filter((order) => selectedIds.has(order.id));
  const toggleSelected = (id: string) => setSelectedIds((old) => {
    const next = new Set(old);
    if (next.has(id)) next.delete(id);
    else if (next.size < 50) next.add(id);
    return next;
  });
  const toggleAllLoaded = () => setSelectedIds((old) => {
    const next = new Set(old);
    const loaded = list.items.slice(0, 50).map((order) => order.id);
    if (loaded.every((id) => next.has(id))) loaded.forEach((id) => next.delete(id));
    else loaded.forEach((id) => { if (next.size < 50) next.add(id); });
    return next;
  });
  useEffect(() => {
    if (ff !== 'tablet' || ledger) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.altKey || event.repeat) return;
      const target = event.target;
      if (target instanceof HTMLElement && (target.isContentEditable || target.closest('input, textarea, select, [contenteditable="true"]'))) return;
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      const key = event.key.toLowerCase();
      if ((event.metaKey || event.ctrlKey) && key === 'a' && !event.shiftKey) {
        if (!list.items.length) return;
        event.preventDefault();
        setMulti(true);
        setListCollapsed(false);
        toggleAllLoaded();
        return;
      }
      if (event.metaKey || event.ctrlKey || event.shiftKey || !selectedId || !list.items.some((item) => item.id === selectedId)) return;
      if (key === 'x') {
        event.preventDefault();
        setMulti(true);
        setListCollapsed(false);
        toggleSelected(selectedId);
      }
      if (key === 'enter' && !multi && !(target instanceof HTMLElement && target.closest('button, a, [role="button"]'))) {
        event.preventDefault();
        navigate(`/orders/${selectedId}`);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ff, ledger, list.items, multi, navigate, selectedId]);
  const selectLoadedPhone = () => {
    const loadedIds = list.items.slice(0, 50).map((order) => order.id);
    if (loadedIds.length && loadedIds.every((id) => selectedIds.has(id))) {
      setSelectedIds(new Set());
      setSelectedExtra([]);
      setSelectAllPromptOpen(false);
      return;
    }
    toggleAllLoaded();
    setSelectAllPromptOpen(list.total > loadedIds.length);
  };
  const selectAllMatchingPhone = async () => {
    if (selectingAll) return;
    setSelectingAll(true);
    setBulkError('');
    try {
      const page = await api.get<OrderPage>(listPath, { ...query, page: 1, pageSize: 50, includeStock: 1 });
      setSelectedExtra(page.items);
      setSelectedIds(new Set(page.items.map((order) => order.id)));
      setSelectAllPromptOpen(false);
    } catch (cause) { setBulkError(cause instanceof Error ? cause.message : '跨页选择失败'); }
    finally { setSelectingAll(false); }
  };
  const reviewable = selectedItems.filter((order) => order.status === 'SUBMITTED');
  const assignable = selectedItems.filter((order) => ['APPROVED', 'IN_PREPARATION', 'STOCK_READY', 'ASSEMBLING', 'ASSEMBLED', 'PARTIALLY_SHIPPED'].includes(order.status));
  const bulkAssign = async () => {
    if (bulkBusy || !assignable.length || !followerId || import.meta.env.VITE_READONLY !== '0' || !can('orders.assign')) return;
    setBulkBusy(true); setBulkError(''); setBulkDone('');
    const assigned: string[] = [];
    try {
      for (const order of assignable) {
        await assignOrderFollower(order.id, followerId, order.followerUser?.id || null);
        assigned.push(order.id);
      }
      setBulkDone(`已分配 ${assigned.length} 张订单。`);
    } catch (cause) {
      setBulkError(`${cause instanceof Error ? cause.message : '分配失败'}；已成功 ${assigned.length} 张。请核对订单后再处理剩余项。`);
    } finally {
      if (assigned.length) {
        setSelectedIds((old) => new Set([...old].filter((id) => !assigned.includes(id))));
        setSelectedExtra((old) => old.filter((order) => !assigned.includes(order.id)));
        void list.reload();
      }
      setBulkBusy(false);
    }
  };
  const bulkApprove = async () => {
    if (bulkBusy || !reviewable.length || !followerId || import.meta.env.VITE_READONLY !== '0' || !can('orders.approve') || !can('orders.assign')) return;
    setBulkBusy(true); setBulkError(''); setBulkDone('');
    const approved: string[] = [];
    try {
      for (const order of reviewable) {
        await approveOrderWithFollower(order.id, followerId);
        approved.push(order.id);
      }
      setBulkDone(`已通过并分配 ${approved.length} 张订单。`);
    } catch (cause) {
      setBulkError(`${cause instanceof Error ? cause.message : '审核失败'}；已成功 ${approved.length} 张。请核对订单状态后再处理剩余项。`);
    } finally {
      if (approved.length) {
        setSelectedIds((old) => new Set([...old].filter((id) => !approved.includes(id))));
        setSelectedExtra((old) => old.filter((order) => !approved.includes(order.id)));
        void list.reload();
      }
      setBulkBusy(false);
    }
  };
  const bulkReject = async () => {
    const reason = rejectReason.trim();
    if (bulkBusy || !reviewable.length || !reason || reason.length > 500 || import.meta.env.VITE_READONLY !== '0' || !can('orders.approve')) return;
    setBulkBusy(true); setBulkError(''); setBulkDone('');
    const rejected: string[] = [];
    try {
      for (const order of reviewable) {
        await rejectOrder(order.id, reason);
        rejected.push(order.id);
      }
      setBulkDone(`已驳回 ${rejected.length} 张订单，解除库存预占并退回草稿。`);
      setRejectReason('');
    } catch (cause) {
      setBulkError(`${cause instanceof Error ? cause.message : '驳回失败'}；已完成 ${rejected.length} 张。请刷新确认后处理剩余项。`);
    } finally {
      if (rejected.length) {
        setSelectedIds((old) => new Set([...old].filter((id) => !rejected.includes(id))));
        setSelectedExtra((old) => old.filter((order) => !rejected.includes(order.id)));
        void list.reload();
      }
      setBulkBusy(false);
    }
  };
  const exportSelected = () => {
    const rows = [['订单号', '1688订单号', '客户', '状态', '订单内容', ...(can('orders.amount.read') ? ['金额'] : [])],
      ...selectedItems.map((order) => [order.orderNo, order.platformOrderNo || '', order.customer.name, orderStatus[order.status].label, summarizeLines(order.lines).map((line) => `${line.name} ${line.qty}`).join('；'), ...(can('orders.amount.read') ? [order.totalAmount === undefined ? '' : String(order.totalAmount)] : [])])];
    const csv = '\uFEFF' + rows.map((row) => row.map((value) => `"${String(value).replaceAll('"', '""')}"`).join(',')).join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = '选中订单.csv';
    anchor.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const cards = (
    <div className={s.cards}>
      {(!body || list.loading) && <div className={s.listbar}>
        <span>{list.loading && !list.items.length ? '加载中…' : viewDef.key === 'tasks' ? `${list.total} 条 · 分配给我或由我提交` : `${list.total} 条${keyword ? ` · 搜索「${keyword}」` : ''}`}</span>
        {ff === 'phone' && viewDef.key === 'tasks' && <button type="button" className={s.cardSettingsButton} onClick={() => setCardSettingsOpen(true)}><span>交货期</span><Icon name="sort" size={14} /></button>}
      </div>}
      {body ||
        list.items.map((o) =>
          ledger && ff === 'phone' && !multi ? (
            <LedgerCard key={o.id} o={o} to={`/orders/${o.id}`} />
          ) : ff === 'phone' && !multi ? (
            <SwipeOrderCard key={o.id} order={o} settings={cardSettings} open={swipedOrderId === o.id} canApprove={can('orders.approve') && can('orders.assign')} onOpen={setSwipedOrderId} onLongPress={() => { setSwipedOrderId(null); setMulti(true); setSelectedIds(new Set([o.id])); }} />
          ) : (
            <WorkCard key={o.id} o={o} to={ff === 'tablet' ? `/orders?${withId(sp, o.id)}` : `/orders/${o.id}`} selected={!multi && o.id === selectedId} selectable={(ff === 'tablet' || ff === 'phone') && multi} checked={selectedIds.has(o.id)} onSelect={() => toggleSelected(o.id)} onLongPress={ff === 'phone' && !multi ? () => { setMulti(true); setSelectedIds(new Set([o.id])); } : undefined} settings={ff === 'phone' ? cardSettings : undefined} />
          ),
        )}
      <div ref={sentinel} className={s.more}>{list.hasNext ? (list.loading ? '加载中…' : '上滑加载更多') : list.items.length > 0 ? '没有更多了' : ''}</div>
    </div>
  );

  /* ------------------------------ 手机 ------------------------------ */
  if (ff === 'phone') {
    const failedList = !!list.error && !list.items.length;
    return (
      <div className={`${s.phone} ${multi ? s.phoneMulti : ''}`}>
        <header className={s.pnb}>{multi ? <><button type="button" className={s.multiCancel} onClick={() => { setMulti(false); setSelectedIds(new Set()); setSelectedExtra([]); setBulkMoreOpen(false); setSelectAllPromptOpen(false); }}>取消</button><h1>已选 {selectedItems.length} 项</h1><button type="button" className={s.multiAll} disabled={selectingAll} onClick={selectLoadedPhone}>{selectingAll ? '选择中…' : '全选'}</button></> : <>
          <h1>订单</h1>
          <IconButton name="srch" label="搜索" onClick={() => setSearching((x) => !x)} />
          {!failedList && <IconButton name="select" label="选择订单" onClick={() => setMulti(true)} />}
          {!failedList && can('orders.create') && <Link className={s.pIcon} to="/orders/new" aria-label="新建订单"><Icon name="plus" size={22} /></Link>}
        </>}</header>
        {searching && <div className={s.psearch}>{searchBox}</div>}
        <div className={s.chipbar}>{chips}{!multi && filterBtn}</div>
        {applied}
        {multi && selectedItems.length > 0 && <div className={s.multiInfo}>{selectAllPromptOpen ? <><span>已选当前页 {Math.min(list.items.length, 50)} 条</span><button type="button" disabled={selectingAll} onClick={() => void selectAllMatchingPhone()}>{selectingAll ? '选择中…' : `选择全部 ${Math.min(list.total, 50)} 条`}</button></> : <>已选 {selectedItems.length} 条 · 单批最多 50 张</>}</div>}
        <div className={`${s.pbody} ${body && !list.loading ? s.phoneStateBody : ''}`}>{cards}</div>
        {multi && <div className={s.phoneSelection}>
          <div><b>已选 {selectedItems.length} 张</b><small>{reviewable.length} 张可审核 · {assignable.length} 张可分配</small></div>
          <button type="button" disabled={!selectedItems.length} onClick={exportSelected}>导出</button>
          <button type="button" disabled={!reviewable.length || !followerId || bulkBusy || import.meta.env.VITE_READONLY !== '0'} onClick={() => void bulkApprove()}>{bulkBusy ? '审核中…' : '批量审核'}</button>
          <button type="button" aria-expanded={bulkMoreOpen} onClick={() => setBulkMoreOpen((open) => !open)}>更多</button>
          {bulkMoreOpen && <div className={s.phoneBulkBackdrop} onMouseDown={(event) => { if (event.target === event.currentTarget) setBulkMoreOpen(false); }}>
            <section className={s.phoneBulkDialog} role="dialog" aria-modal="true" aria-label="更多批量操作">
              <header><h2>更多批量操作</h2><button type="button" aria-label="关闭更多批量操作" onClick={() => setBulkMoreOpen(false)}>关闭</button></header>
              <p>{selectedItems.length} 张已选 · 单批最多 50 张</p>
              <label>统一分配跟单员<select value={followerId} onChange={(event) => setFollowerId(event.target.value)}><option value="">选择跟单员</option>{followers.map((person) => <option key={person.id} value={person.id}>{person.name}</option>)}</select></label>
              <button type="button" disabled={!assignable.length || !followerId || bulkBusy || import.meta.env.VITE_READONLY !== '0'} onClick={() => void bulkAssign()}>{bulkBusy ? '处理中…' : `批量分配（${assignable.length}）`}</button>
              <label>驳回原因<textarea value={rejectReason} onChange={(event) => setRejectReason(event.target.value)} maxLength={500} placeholder="批量驳回时必填，所有选中待审核单共用" /></label>
              <button type="button" disabled={!reviewable.length || !rejectReason.trim() || bulkBusy || import.meta.env.VITE_READONLY !== '0'} onClick={() => void bulkReject()}>批量驳回（{reviewable.length}）</button>
              {(bulkError || bulkDone) && <small role="status">{bulkError || bulkDone}</small>}
            </section>
          </div>}
        </div>}
        {panelEl}
        {cardSettingsOpen && <OrderCardSettingsSheet value={cardSettings} preview={list.items[0]} onClose={() => setCardSettingsOpen(false)} onSave={(value) => {
          setCardSettings(value);
          if (userId) try { localStorage.setItem(`v2:order-card:${userId}`, JSON.stringify(value)); } catch { /* 本机存储不可用时保留本次设置 */ }
          setCardSettingsOpen(false);
        }} />}
      </div>
    );
  }

  /* ------------------------------ iPad ------------------------------ */
  if (ff === 'tablet' && !ledger) {
    const firstLoad = list.loading && !list.items.length;
    const failed = !!list.error && !list.items.length;
    const empty = !firstLoad && !failed && !list.items.length;
    const emptyReview = viewDef.key === 'review' && !keyword && !activeFilters.length;
    const listContent = firstLoad ? <div className={s.ipadSkeletons} aria-hidden="true">{Array.from({ length: 3 }, (_, index) => <div className={s.ipadSkeleton} key={index}>
      <div className={s.ipadCardSkeleton}><i className={s.width46} /><i className={s.width34} /><i className={s.height44} /><i className={s.width62} /></div>
    </div>)}</div>
      : failed ? null
        : empty ? <div className={s.ipadEmptyList}><Empty icon={emptyReview ? 'ok' : 'cards'} title={emptyReview ? '没有等你审核的订单' : keyword || activeFilters.length ? '没有符合条件的订单' : `「${viewDef.label}」里没有订单`} action={<button type="button" className={s.emptyAction} onClick={() => setParam({ view: undefined, id: undefined })}>去我的待办</button>}>
          {emptyReview ? '业务员提交预占后，订单会出现在这里。' : keyword || activeFilters.length ? '换个关键词，或者清空筛选再看看。' : '左侧可以切换视图，或新建一张订单。'}
        </Empty></div> : cards;
    const bulkPanel = <div className={s.bulkPanel}>
      <header><h2>批量操作</h2></header>
      <div className={s.bulkIntro}>
        <div className={s.bulkIcon}><Icon name="check" size={24} /></div>
        <h3>已选 {selectedItems.length} 张订单</h3>
        <p>{selectedItems.length === 0 ? '在左侧勾选需要处理的订单。' : reviewable.length === selectedItems.length ? '都在「等我审核」，都可以审核。统一分配给同一个跟单员。' : `其中 ${reviewable.length} 张可审核，${assignable.length} 张已审核可分配跟单。`}</p>
      </div>
      <div className={s.bulkTableWrap}>
        <table className={s.bulkTable}>
          <thead><tr><th>订单号</th><th>客户</th><th>订单内容</th><th>金额</th><th>库存</th></tr></thead>
          <tbody>{selectedItems.map((order) => <tr key={order.id}>
            <td className="tnum">{order.orderNo}</td><td>{order.customer.name}</td>
            <td>{summarizeLines(order.lines).map((line) => `${line.name} × ${line.qty}`).join('；') || '—'}</td>
            <td>{can('orders.amount.read') && order.totalAmount !== undefined ? money(order.totalAmount) : '—'}</td>
            <td>{order.stockStatus ? <Dot tone={stockStatus[order.stockStatus.status].dot}>{stockStatus[order.stockStatus.status].label}</Dot> : '—'}</td>
          </tr>)}</tbody>
        </table>
      </div>
      <div className={s.bulkActions}>
        <label className={s.bulkAssignee}>分配跟单 <select value={followerId} aria-label="统一分配跟单员" onChange={(event) => setFollowerId(event.target.value)}><option value="">选择跟单员</option>{followers.map((person) => <option key={person.id} value={person.id}>{person.name}</option>)}</select></label>
        <div className={s.bulkActionRow}>
          <button type="button" disabled={!selectedItems.length} onClick={exportSelected}>导出</button>
          <details className={s.bulkRejectReason}><summary>驳回原因</summary><input value={rejectReason} onChange={(event) => setRejectReason(event.target.value)} maxLength={500} placeholder="批量驳回时必填" /></details>
          <button type="button" disabled={!reviewable.length || !rejectReason.trim() || bulkBusy || import.meta.env.VITE_READONLY !== '0'} onClick={() => void bulkReject()}>批量驳回</button>
          {assignable.length > 0 && <button type="button" disabled={!followerId || bulkBusy || import.meta.env.VITE_READONLY !== '0'} onClick={() => void bulkAssign()}>{bulkBusy ? '处理中…' : `批量分配（${assignable.length}）`}</button>}
          <button type="button" disabled={!reviewable.length || !followerId || bulkBusy || import.meta.env.VITE_READONLY !== '0'} onClick={() => void bulkApprove()}>{bulkBusy ? '审核中…' : reviewable.length < selectedItems.length ? `可审核 ${reviewable.length} 张` : `批量通过（${reviewable.length}）`}</button>
        </div>
      </div>
      <p className={s.bulkNote}>{bulkError || bulkDone || `单批上限 50 张。选中里有不能审核的，按钮上写「可审核 N 张」，只处理能处理的；执行后回执成功 / 失败条数。${reviewable.length ? '驳回需要填写原因。' : ''}`}</p>
    </div>;
    const detailContent = multi ? bulkPanel : firstLoad ? <div className={s.ipadDetailSkeleton} aria-hidden="true">
      <div className={s.ipadSkeletonHeader}><i /></div>
      <div className={s.ipadSkeletonBody}>
        <div className={s.ipadSkeletonPanel}><i className={s.width60} /><i className={s.height40} /></div>
        <div className={s.ipadSkeletonPanel}><i className={s.height56} /></div>
        <div className={s.ipadSkeletonPanel}><i className={s.width30} /><i /><i /><i /><i className={s.width80} /></div>
      </div>
    </div>
      : failed ? <div className={s.ipadError}>
        <WifiOffMark />
        <h4>订单列表加载失败</h4>
        <p>{list.error instanceof ApiError && list.error.status === 0 ? '网络连接失败，请检查网络后重试。' : list.error!.message}</p>
        {list.error instanceof ApiError && list.error.status === 0 && <div className={s.ipadRetryInfo}><strong>已自动重试 3 次，仍未成功</strong><span>间隔 0、250、750 毫秒</span></div>}
        <code className={s.ipadErrorCode}>GET {listRequest}</code>
        <div className={s.ipadErrorActions}><Link to="/">返回主屏</Link><button type="button" onClick={list.reload}>重试</button></div>
      </div>
        : empty ? <div className={s.ipadEmptyDetail}><div><div className={s.ipadEmptyDetailIcon}><Icon name="inbox" size={24} /></div>这个视图里没有订单<br /><span>左侧切换视图，或新建一张订单</span></div></div>
          : selectedId ? <OrderDetail id={selectedId} embedded sidebarCollapsed={listCollapsed} onToggleSidebar={() => setListCollapsed((value) => !value)} />
            : <Empty icon="cards" title="选择左侧订单查看详情">详情在这里打开，不用跳页。</Empty>;
    return (
      <div className={`${s.split} ${listCollapsed && !multi && selectedId && list.items.length ? s.focused : ''}`}>
        <section className={s.listCol}>
          <header className={s.lh}>
            {multi ? <><button type="button" className={s.cancelMulti} onClick={() => { setMulti(false); setSelectedIds(new Set()); }}>取消</button><h1>已选 {selectedItems.length} 项</h1><button type="button" className={s.selectAll} onClick={toggleAllLoaded}>全选</button></> : <>{tabletViewPicker}<button type="button" className={s.multiBtn} onClick={() => { setMulti(true); setListCollapsed(false); }} aria-label="批量选择订单" title="批量选择"><Icon name="select" size={20} /></button>{tabletNewOrder}</>}
          </header>
          <div className={s.lsearch}>{searchBox}{filterBtn}</div>
          {applied}
          <div className={s.lbody}>{listContent}</div>
        </section>
        <div className={s.divider} />
        <section className={s.detailCol}>
          {detailContent}
        </section>
        {panelEl}
      </div>
    );
  }

  /* --------------------------- 桌面 / iPad 表格 --------------------------- */
  if (ff === 'desktop') {
    const firstLoad = list.loading && !list.items.length;
    const failed = !!list.error && !list.items.length;
    const reviewEmpty = viewDef.key === 'review' && !firstLoad && !failed && !list.items.length && !keyword && !activeFilters.length;
    const icons: Record<ViewKey, string> = {
      tasks: 'flag', all: 'book', review: 'check', changes: 'alert', ack: 'flag', changeReview: 'alert',
      shortage: 'inbox', ready: 'truck', assembling: 'box', partial: 'truck',
      exceptions: 'alert', unassigned: 'team',
    };
    const sourceViewOrder: ViewKey[] = ['tasks', 'review', 'ready', 'assembling', 'shortage', 'changes', 'exceptions', 'all'];
    const desktopPrimaryViews = sourceViewOrder.map((key) => views.find((item) => item.key === key)).filter((item): item is typeof views[number] => !!item);
    const reviewEmptyViewKeys = new Set<ViewKey>(['tasks', 'review', 'ready', 'assembling', 'shortage', 'all']);
    const visibleDesktopPrimaryViews = reviewEmpty ? desktopPrimaryViews.filter((item) => reviewEmptyViewKeys.has(item.key)) : desktopPrimaryViews;
    const desktopExtraViews = views.filter((item) => !sourceViewOrder.includes(item.key));
    const extraVisible = desktopExtraViewsOpen || desktopExtraViews.some((item) => item.key === viewDef.key);
    const desktopViews = [...visibleDesktopPrimaryViews.map((item) => {
      const count = dateActive ? (item.key === viewDef.key ? list.total : -1) : item.key === 'tasks' ? (viewDef.key === 'tasks' ? list.total : taskCount.data?.pagination.total ?? -1) : stats ? item.count(stats) : -1;
      return { key: item.key, label: item.key === 'shortage' ? '缺货待采' : item.label, icon: icons[item.key], count: count >= 0 ? count : undefined, hot: ['review', 'changes', 'exceptions'].includes(item.key), separator: item.key === 'changes' || item.key === 'all' };
    }), ...(!reviewEmpty && desktopExtraViews.length ? [{ key: '__more', label: extraVisible ? '收起其他视图' : '更多视图', icon: extraVisible ? 'chevu' : 'chevd', separator: true }] : []), ...(!reviewEmpty && extraVisible ? desktopExtraViews.map((item) => {
      const count = dateActive ? (item.key === viewDef.key ? list.total : -1) : stats ? item.count(stats) : -1;
      return { key: item.key, label: item.label, icon: icons[item.key], count: count >= 0 ? count : undefined };
    }) : [])];
    const failedViews = [
      { key: 'tasks', label: '我的待办', icon: icons.tasks, countLabel: '—' },
      { key: 'review', label: '等我审核', icon: icons.review, countLabel: '—' },
      { key: 'ready', label: '可发货', icon: icons.ready, countLabel: '—' },
      { key: 'all', label: '全部订单', icon: icons.all, countLabel: '—', separator: true },
    ];
    return (
      <div className={s.desktopPage}>
        <header className={s.desktopHead}>
          <div><h1>{viewDef.label}</h1><p>{viewDef.key === 'tasks' ? '分配给我或由我提交、当前还需要推进的订单' : ledger ? '完整台账。19 列 + 7 组筛选，查账场景用' : viewDef.key === 'review' ? '已提交预占、等待审核并分配跟单的订单' : '订单工作视图'}<span className="tnum">{firstLoad ? '加载中…' : failed ? '' : `共 ${list.total} 条`}</span></p></div>
          <div className={s.desktopActions}>{firstLoad ? <button type="button" className={s.columnBtn} disabled><Icon name="cog2" />列设置</button> : columnControl}<Button icon="ref" disabled={firstLoad} onClick={list.reload}>{failed ? '重试' : '刷新'}</Button>{firstLoad ? can('orders.create') && <button type="button" className={s.newBtn} disabled><Icon name="plus" />新建订单</button> : !failed && newOrder}</div>
        </header>
        <div className={s.desktopWork}>
            {firstLoad ? <aside className={s.skeletonViewbar} aria-label="视图加载中"><div>视图</div><section>{[80, 60, 100, 60, 80].map((width, index) => <i key={index} style={{ width: `${width}%` }} />)}</section></aside> : <ViewSidebar title="视图" active={viewDef.key} onPick={(key) => key === '__more' ? setDesktopExtraViewsOpen((open) => !open) : setParam({ view: key === 'tasks' ? undefined : key, id: undefined })} views={desktopFailedTasks ? failedViews : desktopViews} />}
          <section className={s.desktopArea}>
            {!desktopFailedTasks && <div className={[s.desktopToolbar, reviewEmpty && s.reviewEmptyToolbar].filter(Boolean).join(' ')}>{firstLoad ? <i className={s.skeletonSearch} /> : <>{searchBox}{!reviewEmpty && filterBtn}{ledger && <div className={s.colPreset} role="group" aria-label="台账列组"><span>列组</span>{LEDGER_PRESETS.map((preset) => <button key={preset.key} type="button" className={ledgerPreset === preset.key ? s.colPresetOn : ''} aria-pressed={ledgerPreset === preset.key} onClick={() => setParam({ cols: preset.key === 'full' ? undefined : preset.key })}>{preset.label}</button>)}</div>}{!reviewEmpty && <><span className={s.desktopSelected}>已选 <b className="tnum">{selectedItems.length}</b> 项</span><span className={s.sp} /><button type="button" className={s.desktopToolButton} disabled={!selectedItems.length} onClick={() => setMulti(true)}>批量审核</button><button type="button" className={s.desktopToolButton} disabled={!selectedItems.length} onClick={exportSelected}>导出</button></>}</>}</div>}
            {applied}
            {body ? <div className={s.dbody}>{body}</div> : <OrdersTable items={list.items} ledger={ledger} preset={ledgerPreset} columnKeys={ledgerKeys} desktopActions selection={{ selectedIds, onToggle: toggleSelected, onToggleAll: toggleAllLoaded }} />}
            {firstLoad && <footer className={s.pager}>正在加载…</footer>}
            {!body && <footer className={s.pager}>
              <span>第 {(list.page - 1) * 30 + 1}–{(list.page - 1) * 30 + list.items.length} 条 / 共 {list.total} 条</span>
              <span className={s.sp} />
              <Button size="sm" disabled={list.page <= 1} onClick={() => list.goto(list.page - 1)}>上一页</Button>
              <span className="tnum">{list.page}</span>
              <Button size="sm" disabled={!list.hasNext} onClick={() => list.goto(list.page + 1)}>下一页</Button>
            </footer>}
          </section>
        </div>
        {multi && <div className={s.desktopBulkBackdrop} onMouseDown={(event) => { if (event.target === event.currentTarget) setMulti(false); }}><section className={s.desktopBulkDialog} role="dialog" aria-modal="true" aria-label="批量审核订单"><header><h2>批量操作</h2><button type="button" onClick={() => setMulti(false)} aria-label="关闭批量操作">×</button></header><p>已选 {selectedItems.length} 张订单，其中 {reviewable.length} 张可审核、{assignable.length} 张可分配。单批最多 50 张。</p><label>统一分配跟单员<select value={followerId} onChange={(event) => setFollowerId(event.target.value)}><option value="">选择跟单员</option>{followers.map((person) => <option key={person.id} value={person.id}>{person.name}</option>)}</select></label><label>驳回原因<input value={rejectReason} onChange={(event) => setRejectReason(event.target.value)} maxLength={500} placeholder="批量驳回时必填" /></label>{(bulkError || bulkDone) && <p role="status">{bulkError || bulkDone}</p>}<footer><button type="button" onClick={() => setMulti(false)}>取消</button><button type="button" disabled={!reviewable.length || !rejectReason.trim() || bulkBusy || import.meta.env.VITE_READONLY !== '0'} onClick={() => void bulkReject()}>批量驳回</button><button type="button" disabled={!assignable.length || !followerId || bulkBusy || import.meta.env.VITE_READONLY !== '0'} onClick={() => void bulkAssign()}>批量分配</button><button type="button" disabled={!reviewable.length || !followerId || bulkBusy || import.meta.env.VITE_READONLY !== '0'} onClick={() => void bulkApprove()}>批量通过</button></footer></section></div>}
        {panelEl}
      </div>
    );
  }

  return (
    <div className={s.desk}>
      <header className={s.dh}>
        {ff === 'tablet' ? tabletViewPicker : <h1>{viewDef.label}<small className="tnum">共 {list.total} 条</small></h1>}
        <span className={s.sp} />
        {searchBox}
        {filterBtn}
        {columnControl}
        <Button icon="ref" onClick={list.reload}>刷新</Button>
        {ff === 'tablet' ? tabletNewOrder : newOrder}
      </header>
      {ff !== 'tablet' && <div className={s.dchips}>{chips}</div>}
      {ff === 'tablet' && ledger && !body && <div className={s.tabletLedgerCount}>{list.total} 条 · 按下单日期倒序</div>}
      {applied}
      {ledger && ff === 'tablet' && selectedItems.length > 0 && <div className={s.tableSelection}><span>已选 {selectedItems.length} 张订单</span><button type="button" onClick={exportSelected}>导出所选</button><button type="button" onClick={() => setSelectedIds(new Set())}>取消选择</button></div>}
      {body ? <div className={s.dbody}>{body}</div> : <OrdersTable items={list.items} ledger={ledger} tabletLedger={ff === 'tablet'} columnKeys={ledgerKeys} onMore={ff === 'tablet' && ledger ? (order) => setLedgerActionId(order.id) : undefined} selection={ff === 'tablet' && ledger ? { selectedIds, onToggle: toggleSelected, onToggleAll: toggleAllLoaded } : undefined} />}
      {!body && (
        <footer className={s.pager}>
          <span>第 {(list.page - 1) * pageSize + 1}–{(list.page - 1) * pageSize + list.items.length} 条 / 共 {list.total} 条</span>
          <span className={s.sp} />
          <Button size="sm" disabled={list.page <= 1} onClick={() => list.goto(list.page - 1)}>上一页</Button>
          <span className="tnum">{list.page}</span>
          <Button size="sm" disabled={!list.hasNext} onClick={() => list.goto(list.page + 1)}>下一页</Button>
        </footer>
      )}
      {panelEl}
      {ff === 'tablet' && ledgerActionId && <OrderLedgerActions key={ledgerActionId} id={ledgerActionId} onClose={() => setLedgerActionId(null)} onChanged={list.reload} />}
    </div>
  );
}

function withId(sp: URLSearchParams, id: string) {
  const n = new URLSearchParams(sp);
  n.set('id', id);
  return n.toString();
}
